"use client"

import { useActionState, useEffect, useState } from "react"
import { Button } from "@ui/button"
import { fetchCountries } from "domains/registration/components/Form"
import { IndividualSubForm } from "./IndividualSubForm"
import { OrganizationSubForm } from "./OrganizationSubForm"
import { registerAction, RegisterState } from "../actions/register"
import { ErrorMessage, Field } from "@ui/fieldset"
import { CheckInbox } from "./CheckInbox"
import { DiscoveredOrganizations } from "./DiscoveredOrganizations"

type AccountType = "organization" | "individual"

function TypeToggle({
    type,
    setType,
}: {
    type: AccountType | undefined
    setType: (type: AccountType | undefined) => void
}) {
    return (
        <span className="grid grid-cols-2 rounded-md shadow-sm">
            <button
                type="button"
                onClick={() => setType("organization")}
                className={`w-full cursor-pointer border border-gray-300 px-4 py-2 text-sm font-semibold focus:outline-none ${
                    type === "organization"
                        ? "bg-primary-800 text-white"
                        : "bg-white text-gray-900 hover:bg-gray-50 dark:bg-zinc-800 dark:text-zinc-100 dark:hover:bg-zinc-700"
                } rounded-l-md`}
            >
                Organization
            </button>
            <button
                type="button"
                onClick={() => setType("individual")}
                className={`w-full cursor-pointer border border-gray-300 px-4 py-2 text-sm font-semibold focus:outline-none ${
                    type === "individual"
                        ? "bg-primary-800 text-white"
                        : "bg-white text-gray-900 hover:bg-gray-50 dark:bg-zinc-800 dark:text-zinc-100 dark:hover:bg-zinc-700"
                } rounded-r-md`}
            >
                Individual
            </button>
        </span>
    )
}

interface Props {
    initialState?: RegisterState
    labs?: boolean
}

export function Form({ initialState = { status: "fresh" }, labs }: Props) {
    const [formState, register, pending] = useActionState(registerAction, initialState)
    const [accountType, setAccountType] = useState<AccountType>()
    const [countries, setCountries] = useState<{ value: string; label: string }[] | undefined>(undefined)
    useEffect(() => {
        fetchCountries().then(setCountries)
    }, [])

    if (formState.status === "ok") {
        return <CheckInbox label="Please check your inbox for an invite email." />
    }

    if (formState.status === "discovered") {
        return <DiscoveredOrganizations formState={formState} />
    }

    return (
        <form action={register}>
            {accountType && <input type="hidden" name="account_type" value={accountType} />}
            {labs === true && <input type="hidden" name="labs" value="yes" />}
            <div className="mx-auto mb-6 w-max">
                <TypeToggle type={accountType} setType={setAccountType} />
            </div>
            {accountType === "individual" && <IndividualSubForm formState={formState} countries={countries} />}
            {accountType === "organization" && <OrganizationSubForm formState={formState} countries={countries} />}
            {formState.status === "error" && Array.isArray(formState.errors?.general) && (
                <>
                    {formState.errors.general.map((error) => (
                        <Field>
                            <ErrorMessage key={error}>{error}</ErrorMessage>
                        </Field>
                    ))}
                </>
            )}
            {accountType !== undefined && (
                <Button disabled={pending} type="submit" color="primary" className="mt-8 w-full">
                    {pending ? "Signing up..." : "Sign Up"}
                </Button>
            )}
        </form>
    )
}
